<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Back nav -->
    <div class="bg-white border-b border-gray-100 sticky top-0 z-10 no-print">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 py-3 tw-flex items-center justify-between gap-4">
        <router-link
          to="/profile/update#personal-home"
          class="tw-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-gray-800 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          Home
        </router-link>
        <button
          @click="exportPDF"
          class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Export PDF
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="max-w-4xl mx-auto px-4 sm:px-6 py-16 tw-flex justify-center">
      <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>

    <div v-else-if="!pulseData" class="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center text-gray-400">
      No Transition Pulse found for this period.
    </div>

    <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      <!-- Header -->
      <div>
        <p class="text-xs font-semibold uppercase tracking-widest text-primary mb-1 m-0">{{ monthLabel }}</p>
        <h1 class="text-2xl font-bold text-gray-900 m-0">Transition Pulse</h1>
      </div>

      <!-- Category tabs -->
      <div class="no-print tw-flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        <button
          v-for="cat in categories"
          :key="cat.key"
          @click="activeCategory = cat.key; expandedSignals = {}"
          :class="[
            'flex-1 max-w-[250px] min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-all',
            activeCategory === cat.key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          {{ cat.label }}
        </button>
      </div>

      <template v-if="activeData">

        <!-- Domain title -->
        <h2 class="text-xl font-bold text-gray-900 m-0">{{ activeData.title }}</h2>

        <!-- 1. Key Judgment -->
        <div class="space-y-1">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 m-0">Key Judgment</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0">{{ activeData.key_judgment }}</p>
        </div>

        <!-- 2. Why This Month Matters -->
        <div class="space-y-1">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 m-0">Why this month matters</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0">{{ activeData.why_this_matters }}</p>
        </div>

        <!-- 3. What is driving the transition now — 4 blocks -->
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-4 m-0">What is driving the transition now</p>
          <div class="grid gap-4 sm:grid-cols-2">
            <div
              v-for="group in drivingGroups"
              :key="group.key"
              class="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
            >
              <p class="text-xs font-semibold text-primary mb-3 m-0">{{ group.label }}</p>
              <div class="space-y-3">
                <div
                  v-for="(signal, i) in activeData.driving_signals[group.key]"
                  :key="i"
                  class="rounded-lg"
                  :class="{ 'bg-gray-50': expandedSignals[group.key + '_' + i] }"
                >
                  <!-- Signal row -->
                  <div
                    class="tw-flex items-start gap-1.5 rounded-lg px-2 py-1.5 -mx-2 transition-colors"
                    :class="signal.articles && signal.articles.filter(a => a.url).length ? 'cursor-pointer hover:bg-gray-50' : ''"
                    @click="signal.articles && signal.articles.filter(a => a.url).length && toggleSignal(group.key, i)"
                  >
                    <span :class="['text-sm font-bold shrink-0 mt-px m-auto justify-center self-center text-center mr-1', trendColorClass(signal.trend)]" style="width: 25px;">
                      {{ trendArrow(signal.trend) }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <span class="text-sm font-semibold text-gray-900 leading-snug">{{ signal.name }}</span>
                      <p v-if="signal.meta" class="text-xs text-gray-400 m-0 mt-0.5">{{ signal.meta }}</p>
                    </div>
                    <svg
                      v-if="signal.articles && signal.articles.filter(a => a.url).length"
                      class="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1 transition-transform duration-200"
                      :class="{ 'rotate-180': expandedSignals[group.key + '_' + i] }"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>

                  <!-- Articles (collapsible) -->
                  <div
                    v-if="signal.articles && signal.articles.filter(a => a.url).length && expandedSignals[group.key + '_' + i]"
                    class="mt-1 pb-2 px-2 space-y-1"
                  >
                    <a
                      v-for="article in signal.articles.filter(a => a.url)"
                      :key="article.url_id"
                      :href="article.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="tw-flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-gray-100 hover:border-gray-300 transition-colors no-underline group"
                    >
                      <svg class="w-3 h-3 text-gray-300 shrink-0 group-hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      <span class="text-xs text-gray-600 leading-snug flex-1 min-w-0 truncate group-hover:text-gray-900 transition-colors">
                        {{ article.title || articleDomain(article.url) || 'Article' }}
                      </span>
                      <svg v-if="article.url" class="w-3 h-3 text-gray-300 shrink-0 group-hover:text-gray-400 transition-colors" fill="none" stroke="currentColor"
                           viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Actors in motion -->
        <div v-if="activeData.actors && activeData.actors.length">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3 m-0">Change makers in motion</p>
          <div class="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
            <component
              :is="actor.elastic_id ? 'router-link' : 'div'"
              v-for="(actor, i) in activeData.actors"
              :key="i"
              :to="actor.elastic_id ? '/actors/' + actor.elastic_id : undefined"
              class="tw-flex items-center gap-3 px-4 py-3 no-underline hover:bg-gray-50 transition-colors group"
            >
              <div class="w-1.5 h-1.5 rounded-full bg-primary shrink-0 opacity-60"/>
              <div class="min-w-0 flex-1">
                <span class="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">{{ actor.name }}</span>
                <p
                  v-if="actor.description"
                  class="text-xs text-gray-400 m-0 line-clamp-2"
                >
                  {{ actor.description }}
                </p>
              </div>
              <svg v-if="actor.elastic_id" class="w-3.5 h-3.5 text-gray-300 group-hover:text-primary transition-colors shrink-0" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </component>
          </div>
        </div>

      </template>

    </div>
  </div>
</template>

<script>

  // TODO: remove once backend is ready
  import { fetchTransitionPulse } from '@/api/transition-pulse'

  const MOCK_PULSE_DATA = {
    protein: {
      title: 'Protein Shift',
      key_judgment: 'Standing Ovation\'s €30 million Series B funding underscores robust investor confidence in the alternative protein market, signaling a pivotal shift towards mainstream acceptance and commercialisation of sustainable protein solutions.',
      why_this_matters: 'Standing Ovation raised €30 million to scale dairy proteins via precision fermentation, accelerating commercialisation in North America. The UK government emphasised alternative proteins in its food strategy, positioning itself as a leader. Schouten Europe launched a new plant-based fillet improving texture and sustainability.',
      driving_signals: {
        capital_investment: [
          { trend: 'up', name: 'Precision fermentation scaling', meta: 'Standing Ovation €30M funding' },
          { trend: 'up', name: 'Sector moving to mainstream', meta: 'Focus on taste, price, health' },
          { trend: 'down', name: 'Financing gap', meta: '€62B unmet demand among EU farmers' },
        ],
        technology_innovation: [
          { trend: 'up', name: 'Product performance improving', meta: 'Fiber tech improving texture' },
          { trend: 'up', name: 'Open innovation', meta: 'Corporate-startup partnerships' },
          { trend: 'up', name: 'Production scaling', meta: 'Mycoprotein facilities expanding' },
        ],
        policy_regulatory: [
          { trend: 'up', name: 'UK strategic positioning', meta: 'Alternative proteins in food strategy' },
          { trend: 'down', name: 'EU regulatory constraints', meta: 'Labeling restrictions tightening' },
        ],
        consumer_market: [
          { trend: 'up', name: 'Adoption growth', meta: 'Italy +11% plant-based sales' },
          { trend: 'neutral', name: 'Gradual shift', meta: 'Health-driven demand rising slowly' },
        ],
      },
      actors: [
        { name: 'Standing Ovation', meta: 'Scaling precision fermentation', elastic_id: 'standing_ovation' },
        { name: 'Danone', meta: 'Strategic protein investments', elastic_id: 'danone_004' },
        { name: 'Arla Foods', meta: 'Policy and innovation engagement', elastic_id: 'arla_foods' },
        { name: 'Bel Group', meta: 'Alternative protein portfolio', elastic_id: 'bel_group' },
        { name: 'nextProtein', meta: 'Scaling insect proteins', elastic_id: 'nextprotein' },
      ],
    },
  }

  const CATEGORY_LABELS = {
    protein: 'Protein',
    health: 'Health',
    upcycling: 'Upcycling',
    circular: 'Circular',
  }

  const DRIVING_GROUPS = [
    { key: 'capital_investment', label: 'Capital & Investment' },
    { key: 'technology_innovation', label: 'Technology & Innovation' },
    { key: 'policy_regulatory', label: 'Policy & Regulatory' },
    { key: 'consumer_market', label: 'Consumer & Market' },
  ]

  export default {
    name: 'TransitionPulseDetail',

    data () {
      return {
        isLoading: true,
        pulseData: null,
        activeCategory: null,
        expandedSignals: {},
      }
    },

    computed: {
      year () {
        return Number(this.$route.params.year)
      },
      month () {
        return Number(this.$route.params.month)
      },
      monthLabel () {
        if (!this.year || !this.month) return ''
        return new Date(this.year, this.month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      },
      categories () {
        if (!this.pulseData) return []
        return Object.keys(this.pulseData)
          .filter(key => {
            const val = this.pulseData[key]
            return val && !Array.isArray(val) && Object.keys(val).length > 0
          })
          .map(key => ({
            key,
            label: CATEGORY_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1)),
          }))
      },
      activeData () {
        if (!this.pulseData || !this.activeCategory) return null
        const d = this.pulseData[this.activeCategory]
        return Array.isArray(d) ? d[0] : d
      },
      drivingGroups () {
        return DRIVING_GROUPS
      },
    },

    methods: {
      toggleSignal (groupKey, index) {
        const key = groupKey + '_' + index
        this.expandedSignals = { ...this.expandedSignals, [key]: !this.expandedSignals[key] }
      },
      articleDomain (url) {
        if (!url) return null
        try {
          return new URL(url).hostname.replace(/^www\./, '')
        } catch {
          return null
        }
      },
      trendArrow (trend) {
        const map = { 'double-up': '↑↑', up: '↑', neutral: '→', down: '↓', 'double-down': '↓↓' }
        return map[trend] || '→'
      },
      trendColorClass (trend) {
        if (trend === 'up' || trend === 'double-up') return 'text-green-600'
        if (trend === 'down' || trend === 'double-down') return 'text-red-500'
        return 'text-gray-400'
      },
      async exportPDF () {
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set({
          margin: [12, 14],
          filename: `transition-pulse-${this.monthLabel.replace(/\s/g, '-').toLowerCase()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }).from(this.buildReportHTML(), 'string').save()
      },
      buildReportHTML () {
        const GROUP_LABELS = {
          capital_investment: 'Capital & Investment',
          technology_innovation: 'Technology & Innovation',
          policy_regulatory: 'Policy & Regulatory',
          consumer_market: 'Consumer & Market',
        }
        const arrow = (t) => ({ 'double-up': '↑↑', up: '↑', neutral: '→', down: '↓', 'double-down': '↓↓' }[t] || '→')
        const arrowColor = (t) => (t === 'up' || t === 'double-up') ? '#16a34a' : (t === 'down' || t === 'double-down') ? '#dc2626' : '#9ca3af'

        const categorySections = Object.entries(this.pulseData || {}).map(([, cat]) => {
          const signalGroups = Object.entries(cat.driving_signals || {}).map(([key, signals]) => `
            <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;">
              <div style="font-size:11px;font-weight:700;color:#15803d;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.05em;">${GROUP_LABELS[key] || key}</div>
              ${(signals || []).map(s => `
                <div style="margin-bottom:8px;">
                  <span style="font-weight:700;font-size:13px;color:${arrowColor(s.trend)};margin-right:4px;">${arrow(s.trend)}</span>
                  <span style="font-size:13px;font-weight:600;color:#111827;">${s.name}</span>
                  ${s.meta ? `<div style="font-size:11px;color:#9ca3af;padding-left:18px;margin-top:1px;">${s.meta}</div>` : ''}
                </div>`).join('')}
            </div>`).join('')

          const actors = (cat.actors || []).map(a =>
            `<div style="font-size:13px;padding:5px 0;border-bottom:1px solid #f3f4f6;">
              <span style="font-weight:600;">${a.name}</span>
              ${a.meta ? ` — <span style="color:#6b7280;">${a.meta}</span>` : ''}
            </div>`
          ).join('')

          return `
            <div style="margin-bottom:40px;page-break-after:always;">
              <h2 style="font-size:20px;font-weight:700;margin-bottom:20px;">${cat.title || ''}</h2>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Key Judgment</div>
                <p style="font-size:13px;color:#374151;line-height:1.7;">${cat.key_judgment || ''}</p>
              </div>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Why This Month Matters</div>
                <p style="font-size:13px;color:#374151;line-height:1.7;">${cat.why_this_matters || ''}</p>
              </div>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:8px;">What is driving the transition now</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">${signalGroups}</div>
              </div>
              ${actors ? `<div>
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Change makers in motion</div>
                ${actors}
              </div>` : ''}
            </div>`
        }).join('')

        return `<div style="width:780px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f2937;line-height:1.6;background:#fff;padding:32px 28px 60px;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #1f2937;padding-bottom:12px;margin-bottom:32px;">
            <div style="font-size:22px;font-weight:700;">Transition Pulse</div>
            <div style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;">${this.monthLabel}</div>
          </div>
          ${categorySections}
        </div>`
      },
      async loadPulse () {
        this.isLoading = true
        try {
          // TODO: replace with real API call once backend is ready
          await fetchTransitionPulse(this.year, this.month)
            .then((response) => {
              this.pulseData = response?.report_content?.categories
            })
          //this.pulseData = MOCK_PULSE_DATA
          if (this.categories.length) this.activeCategory = this.categories[0].key
        } catch (e) {
          this.pulseData = null
        } finally {
          this.isLoading = false
        }
      },
    },

    watch: {
      '$route.params': {
        handler () {
          this.loadPulse()
        },
        immediate: false,
      },
    },

    async mounted () {
      await this.loadPulse()
    },
  }
</script>
